import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { reconcileSelectedBotId } from '../utils/permissions';
import { useUnsavedChanges } from './UnsavedChangesContext.js';

/**
 * Single source of truth for "which bot is the user looking at".
 *
 * Before the restructure every page (Settings, Activity, Live,
 * Integration) fetched the bot list and rendered its own "Select bot"
 * dropdown. Now the bot is chosen once, globally, by the sidebar
 * `BotSelector`, and every section page reads it from here.
 *
 * Selection is persisted to localStorage (`selectedBotId` AND
 * `selectedBotIds`) so the surfaces that still manage their own
 * selection internally (the live inbox, the integration page) stay in
 * sync, and so it survives reloads / workspace switches.
 */
const SelectedBotContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const SelectedBotProvider = ({ children }) => {
  const navigate = useNavigate();
  const { requestUnsavedAction } = useUnsavedChanges();

  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBotId, setSelectedBotIdState] = useState(
    () => localStorage.getItem('selectedBotId') || ''
  );

  // Keep both storage keys in lockstep. `selectedBotId` is the primary
  // (single-select) value; `selectedBotIds` is the array the analytics
  // surfaces and the live inbox read.
  const persist = useCallback((id) => {
    if (!id) return;
    localStorage.setItem('selectedBotId', id);
    localStorage.setItem('selectedBotIds', JSON.stringify([id]));
  }, []);

  const setSelectedBotId = useCallback(
    (id) => {
      if (!id) return;
      setSelectedBotIdState(String(id));
      persist(String(id));
    },
    [persist]
  );

  // `silent` skips the global loading flag so a background refresh (e.g. after
  // the test-chat reports new usage) doesn't blank out pages that gate on
  // `loading` — it just swaps the data in underneath.
  const fetchBots = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const url = `${API_BASE}bots?user_id=${
        localStorage.getItem('user_id') || ''
      }&include_knowledge=False`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      // "Ready" = anything the user can actually open. A finished scrape that
      // found nothing is `no_content_found` (not `completed`) but is still a
      // real, selectable agent — keep it, or its Overview shows the empty
      // "Create Your First Agent" state.
      const readyBots = data
        .filter(
          (bot) => bot.status === 'completed' || bot.status === 'no_content_found'
        )
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setBots(readyBots);

      // Reconcile the stored selection against this workspace's bots so
      // section pages never mount with a stale id (which would 403).
      // reconcileSelectedBotId persists `selectedBotId`; mirror it into
      // `selectedBotIds` too.
      const resolved = reconcileSelectedBotId(readyBots);
      setSelectedBotIdState(resolved || '');
      if (resolved) persist(resolved);
      setError(null);
    } catch (err) {
      console.error('Error fetching bots:', err);
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [persist]);

  useEffect(() => {
    fetchBots();
  }, [fetchBots]);

  // Other surfaces ask us to switch the active bot without going through
  // the BotSelector:
  //  - `ub:select-bot`     — a deep-link/jump (e.g. the bots overview gear
  //                          icon, success-score modal) selects a bot then
  //                          navigates to a section page.
  //  - `live-notify-open`  — the live inbox surfaces a toast for a bot and
  //                          syncs the selection.
  useEffect(() => {
    const onSelect = (e) => {
      const id = e?.detail?.botId;
      if (id) {
        setSelectedBotIdState(String(id));
        persist(String(id));
      }
    };
    window.addEventListener('ub:select-bot', onSelect);
    window.addEventListener('live-notify-open', onSelect);
    return () => {
      window.removeEventListener('ub:select-bot', onSelect);
      window.removeEventListener('live-notify-open', onSelect);
    };
  }, [persist]);

  // The test-chat (ChatModal) fires `ub:usage-updated` when it closes after
  // sending messages. Re-pull the bot list so `selectedBot.current_message_count`
  // — and therefore the sidebar "AI responses" footer — reflects the new usage
  // without a manual refresh.
  useEffect(() => {
    const onUsageUpdated = () => { fetchBots({ silent: true }); };
    window.addEventListener('ub:usage-updated', onUsageUpdated);
    return () => window.removeEventListener('ub:usage-updated', onUsageUpdated);
  }, [fetchBots]);

  // Send the user into the guided agent-creation flow (/agents/new).
  const openAddBot = useCallback(() => {
    requestUnsavedAction(() => navigate('/agents/new'));
  }, [navigate, requestUnsavedAction]);

  const selectedBot = useMemo(
    () => bots.find((b) => String(b.id) === String(selectedBotId)) || null,
    [bots, selectedBotId]
  );

  const value = useMemo(
    () => ({
      bots,
      loading,
      error,
      selectedBotId,
      selectedBot,
      selectedBotIds: selectedBotId ? [selectedBotId] : [],
      setSelectedBotId,
      refreshBots: fetchBots,
      openAddBot,
    }),
    [
      bots,
      loading,
      error,
      selectedBotId,
      selectedBot,
      setSelectedBotId,
      fetchBots,
      openAddBot,
    ]
  );

  return (
    <SelectedBotContext.Provider value={value}>
      {children}
    </SelectedBotContext.Provider>
  );
};

export const useSelectedBot = () => {
  const ctx = useContext(SelectedBotContext);
  if (!ctx) {
    throw new Error('useSelectedBot must be used within SelectedBotProvider');
  }
  return ctx;
};
